"use client"

import { t } from "@/languages/i18n"
import { fetchCategories } from "@/services/category"
import { useDomainStore } from "@/stores/use-domain-store"
import { CategoryData } from "@/types/types"
import { getEnforcedDomainValue } from "@/utils/admin-domain-filter"
import { CloseOutlined } from "@ant-design/icons"
import { Button, Select, Tag } from "antd"
import { Grip } from "lucide-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { ReactSortable } from "react-sortablejs"

interface CategoryItem {
  id: number
  name: string
  publishedAt?: string | null
}

interface CategoriesRelationProps {
  value?: number[]
  onChange?: (categoryIds: number[]) => void
}

const CategoriesRelation: React.FC<CategoriesRelationProps> = ({ value = [], onChange }) => {
  const { selectedDomain } = useDomainStore()
  const enforcedDomain = getEnforcedDomainValue(selectedDomain)
  const [allCategories, setAllCategories] = useState<CategoryData[]>([])
  const [selectedCategories, setSelectedCategories] = useState<CategoryItem[]>([])
  const [loading, setLoading] = useState(false)

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true)
      const params: Record<string, string> = {}
      if (enforcedDomain) params.domain = enforcedDomain
      const response: any = await fetchCategories(params, "preview")
      if (response?.data) {
        setAllCategories(response.data)
      }
    } catch (error) {
      console.error("Failed to load categories:", error)
    } finally {
      setLoading(false)
    }
  }, [enforcedDomain])

  useEffect(() => {
    loadCategories()
  }, [loadCategories])

  // Load selected categories when value changes
  useEffect(() => {
    if (value && value.length > 0 && allCategories.length > 0) {
      const selected: CategoryItem[] = value
        .map(id => {
          const category = allCategories.find(cat => cat.id === id)
          if (category) {
            return {
              id: category.id,
              name: category.attributes.name,
              publishedAt: category.attributes.publishedAt,
            } as CategoryItem
          }
          return null
        })
        .filter((item): item is CategoryItem => item !== null)
      setSelectedCategories(selected)
    } else if (!value || value.length === 0) {
      setSelectedCategories([])
    }
  }, [value, allCategories])

  const handleAddCategory = useCallback(
    (categoryIds: number[]) => {
      const newCategoryIds = categoryIds.filter(id => !value.includes(id))
      const updatedIds = [...value, ...newCategoryIds]
      onChange?.(updatedIds)
    },
    [value, onChange],
  )

  const handleRemoveCategory = useCallback(
    (categoryId: number) => {
      const updatedIds = value.filter(id => id !== categoryId)
      onChange?.(updatedIds)
    },
    [value, onChange],
  )

  const handleSort = useCallback(
    (sortedCategories: CategoryItem[]) => {
      setSelectedCategories(sortedCategories)
      const sortedIds = sortedCategories.map(cat => cat.id)
      onChange?.(sortedIds)
    },
    [onChange],
  )

  const categoryOptions = useMemo(() => {
    return allCategories
      .filter(cat => !value.includes(cat.id))
      .map(category => ({
        value: category.id,
        label: category.attributes.name,
      }))
  }, [allCategories, value])

  return (
    <div className="w-full">
      <Select
        showSearch
        mode="multiple"
        placeholder={t("Add relation")}
        options={categoryOptions}
        value={[]}
        onChange={handleAddCategory}
        style={{ width: "100%" }}
        loading={loading}
        filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
      />
      <div className="text-xs text-gray-500 mt-1 mb-2">{t("Could select more than 1 category")}</div>

      {selectedCategories.length > 0 && (
        <ReactSortable
          list={selectedCategories}
          setList={handleSort}
          handle=".drag-handle"
          animation={150}
          className="flex flex-col gap-2"
        >
          {selectedCategories.map(category => (
            <div
              key={category.id}
              className="flex items-center gap-2 p-2 border border-gray-200 rounded bg-white hover:bg-gray-50"
            >
              <Grip className="drag-handle cursor-grab text-gray-400 hover:text-gray-600" size={16} />
              <span className="flex-1">{category.name}</span>
              <Tag color={category.publishedAt ? "green" : "default"}>
                {category.publishedAt ? t("Published") : t("Draft")}
              </Tag>
              <Button
                type="text"
                danger
                icon={<CloseOutlined />}
                onClick={() => handleRemoveCategory(category.id)}
                size="small"
              />
            </div>
          ))}
        </ReactSortable>
      )}
    </div>
  )
}

export default CategoriesRelation
